import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { obtenerAplicacion, actualizarAplicacion } from '../../services/aplicaciones';
import { isOnline } from '../../services/offline/networkStatus';
import AppShell from '../../components/layout/AppShell';
import AplicacionForm from '../../components/aplicaciones/AplicacionForm';

const OFFLINE_MESSAGE = 'Necesitas conexión a Internet para modificar una aplicación.';

// El productor de una aplicación NUNCA se cambia al editar (decisión de la
// fase multi-productor, sección 18): no hace falta cargar la lista de
// productores asignados aquí, solo mostrar el que ya tiene la aplicación
// (aplicacion.productores.nombre, vía join existente en obtenerAplicacion).
function EditarAplicacionPage() {
  const { id } = useParams();
  const { productor } = useAuth();
  const navigate = useNavigate();

  const [aplicacion, setAplicacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    // Editar sigue siendo online-only en esta fase: sin conexión ni
    // siquiera se intenta cargar la aplicación (evita spinner infinito
    // esperando un fetch que ya sabemos que va a fallar).
    if (!isOnline()) {
      setError(OFFLINE_MESSAGE);
      setLoading(false);
      return undefined;
    }

    obtenerAplicacion(id).then((result) => {
      if (!active) return;
      if (result.ok) {
        setAplicacion(result.aplicacion);
      } else {
        setError(result.message);
      }
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [id]);

  const handleSubmit = async (payload) => {
    if (!isOnline()) {
      return { ok: false, message: OFFLINE_MESSAGE };
    }
    const result = await actualizarAplicacion(id, payload);
    if (result.ok) {
      navigate(`/aplicaciones/${id}`, { state: { justUpdated: true }, replace: true });
      return result;
    }
    // navigator.onLine no siempre detecta la pérdida real de conexión (ver
    // comentario en services/aplicaciones.js): si el fetch falló por red,
    // se muestra el mismo mensaje amigable en vez del genérico.
    if (result.networkError) {
      return { ok: false, message: OFFLINE_MESSAGE };
    }
    return result;
  };

  if (loading) {
    return (
      <AppShell title="Editar aplicación" backTo={`/aplicaciones/${id}`}>
        <p className="text-center text-sm text-gray-500">Cargando...</p>
      </AppShell>
    );
  }

  if (error || !aplicacion) {
    return (
      <AppShell title="Editar aplicación" backTo="/aplicaciones">
        <p className="text-center text-sm text-red-600">{error || 'Aplicación no encontrada.'}</p>
      </AppShell>
    );
  }

  if (aplicacion.estado !== 'ACTIVA') {
    return (
      <AppShell title="Editar aplicación" backTo={`/aplicaciones/${id}`}>
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-center text-sm text-amber-700">
          Esta aplicación está anulada y no puede editarse.
        </p>
      </AppShell>
    );
  }

  return (
    <AppShell title="Editar aplicación" backTo={`/aplicaciones/${id}`}>
      <AplicacionForm
        mode="edit"
        productorNombre={aplicacion.productores?.nombre || productor?.nombre}
        initialData={{
          huerto: aplicacion.huerto || '',
          cuartel: aplicacion.cuartel || '',
          fechaaplicacion: aplicacion.fechaaplicacion || '',
          horainicio: aplicacion.horainicio ? aplicacion.horainicio.slice(0, 5) : '',
          horafin: aplicacion.horafin ? aplicacion.horafin.slice(0, 5) : '',
          ha: aplicacion.ha ?? '',
          productos: (aplicacion.aplicacion_detalle ?? []).map((p) => ({
            nombrecomercial: p.nombrecomercial || '',
            dosis: p.dosis || '',
          })),
        }}
        onSubmit={handleSubmit}
        cancelTo={`/aplicaciones/${id}`}
      />
    </AppShell>
  );
}

export default EditarAplicacionPage;
